import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Receipt, Trash2, TrendingUp, TrendingDown, Calendar, Filter, Search, X, FileText, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para obtener fecha en zona horaria de Guatemala (GMT-6)
const getGuatemalaDate = () => {
  const now = new Date();
  // Convertir a zona horaria de Guatemala (UTC-6)
  const guatemalaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Guatemala' }));
  return guatemalaTime.toISOString().split('T')[0];
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Estado para diálogo de factura multi-línea
  const [multiLineDialogOpen, setMultiLineDialogOpen] = useState(false);
  const [multiLineForm, setMultiLineForm] = useState({
    store_id: '',
    transaction_type: 'GASTO',
    invoice_number: '',
    invoice_date: getGuatemalaDate(),
    payment_method: 'EFECTIVO',
    bank_id: '',
    lines: [{ account_code: '', description: '', amount: '', has_iva: false, amount_includes_iva: false }]
  });
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [receiptSearchTerm, setReceiptSearchTerm] = useState('');  // Búsqueda por número de boleta
  const [receiptSearchResults, setReceiptSearchResults] = useState(null);  // Resultados de búsqueda
  const [showReceiptSearch, setShowReceiptSearch] = useState(false);  // Mostrar modal de búsqueda
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [pageSize] = useState(100);
  const [tableKey, setTableKey] = useState(0);
  const [banks, setBanks] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [filters, setFilters] = useState({
    store_id: 'all',
    transaction_type: 'all',  // INGRESO, GASTO, o 'all'
    account_code: 'all',      // Filtro por cuenta contable
    start_date: (() => {
      const date = new Date();
      date.setDate(1); // Primer día del mes
      return new Date(date.toLocaleString('en-US', { timeZone: 'America/Guatemala' })).toISOString().split('T')[0];
    })(),
    end_date: getGuatemalaDate()
  });
  const [formData, setFormData] = useState({
    store_id: '',
    account_code: '',
    transaction_type: 'GASTO',
    description: '',
    amount: '',
    has_iva: false,
    amount_includes_iva: false,
    transaction_date: getGuatemalaDate(),
    payment_method: 'EFECTIVO',
    bank_id: '',
    receipt_number: '',  // Número de boleta bancaria
    invoice_number: '',  // Número de factura (obligatorio para gastos)
    supplier_id: '',     // ID del proveedor (para gastos con cuenta de proveedores)
    is_asset: false,
    asset_data: {
      name: '',
      category: '',
      brand: '',
      quantity: '1',
      serial_number: '',
      warranty_months: '',
      purchase_location: '',
      notes: ''
    },
    is_fuel: false,
    fuel_data: {
      gallons: '',
      fuel_type: 'SUPER',
      price_per_gallon: ''
    }
  });

  // Cálculo de IVA en tiempo real
  const [ivaCalculation, setIvaCalculation] = useState({
    base: 0,
    iva: 0,
    total: 0
  });

  useEffect(() => {
    fetchStores();
    fetchAccounts();
    fetchBanks();
    fetchAssetCategories();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [filters, currentPage]);

  // Tarifas de impuesto al petróleo por galón
  const FUEL_TAX_RATES = {
    'SUPER': 4.70,
    'DIESEL': 1.30,
    'REGULAR': 4.60
  };

  // Cálculo de impuesto al petróleo
  const [fuelCalculation, setFuelCalculation] = useState({
    petroleum_tax: 0,
    taxable_base: 0,
    iva: 0,
    total: 0
  });

  // Cálculo de IVA en tiempo real (con manejo de combustibles)
  useEffect(() => {
    if (formData.amount && !isNaN(formData.amount)) {
      const amount = parseFloat(formData.amount);
      
      // Si es combustible, calcular el impuesto al petróleo
      if (formData.is_fuel && formData.fuel_data.gallons && formData.fuel_data.fuel_type) {
        const gallons = parseFloat(formData.fuel_data.gallons);
        const taxRate = FUEL_TAX_RATES[formData.fuel_data.fuel_type];
        const petroleumTax = gallons * taxRate;
        
        // Total factura YA incluye IVA
        // Base gravable = Total - Impuesto Petróleo
        const taxableBase = amount - petroleumTax;
        
        // Calcular IVA: Base incluye IVA, entonces: IVA = Base / 1.12 * 0.12
        const ivaAmount = (taxableBase / 1.12) * 0.12;
        
        setFuelCalculation({
          petroleum_tax: petroleumTax,
          taxable_base: taxableBase,
          iva: ivaAmount,
          total: amount
        });
        
        // Actualizar también el cálculo de IVA normal para mostrar
        setIvaCalculation({
          base: taxableBase - ivaAmount,
          iva: ivaAmount,
          total: amount
        });
      } else {
        // Cálculo normal de IVA (sin combustible)
        if (formData.has_iva) {
          if (formData.amount_includes_iva) {
            const base = amount / 1.12;
            const iva = amount - base;
            setIvaCalculation({
              base: base,
              iva: iva,
              total: amount
            });
          } else {
            const iva = amount * 0.12;
            const total = amount + iva;
            setIvaCalculation({
              base: amount,
              iva: iva,
              total: total
            });
          }
        } else {
          setIvaCalculation({
            base: amount,
            iva: 0,
            total: amount
          });
        }
        
        // Limpiar cálculo de combustible
        setFuelCalculation({
          petroleum_tax: 0,
          taxable_base: 0,
          iva: 0,
          total: 0
        });
      }
    } else {
      setIvaCalculation({ base: 0, iva: 0, total: 0 });
      setFuelCalculation({ petroleum_tax: 0, taxable_base: 0, iva: 0, total: 0 });
    }
  }, [formData.amount, formData.has_iva, formData.amount_includes_iva, formData.is_fuel, formData.fuel_data.gallons, formData.fuel_data.fuel_type]);

  useEffect(() => {
    // Filtrar transacciones por búsqueda
    if (searchTerm) {
      const filtered = transactions.filter(t =>
        t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.account_code.includes(searchTerm) ||
        getStoreName(t.store_id).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions(transactions);
    }
  }, [searchTerm, transactions]);

  // Buscar por número de boleta
  const searchByReceiptNumber = async () => {
    if (!receiptSearchTerm.trim()) {
      toast.error('Ingresa un número de boleta para buscar');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/transactions/search/receipt`, {
        params: { receipt_number: receiptSearchTerm },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceiptSearchResults(response.data);
    } catch (error) {
      toast.error('Error al buscar por número de boleta');
    }
  };



  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        page: currentPage,
        page_size: pageSize
      };
      
      if (filters.store_id !== 'all') {
        params.store_id = filters.store_id;
      }
      
      if (filters.transaction_type !== 'all') {
        params.transaction_type = filters.transaction_type;
      }
      
      if (filters.account_code !== 'all') {
        params.account_code = filters.account_code;
      }
      
      console.log('Fetching transactions with params:', params);
      
      // Obtener conteo total primero
      const countResponse = await axios.get(`${API}/transactions/stats/count`, {
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          store_id: filters.store_id !== 'all' ? filters.store_id : undefined,
          transaction_type: filters.transaction_type !== 'all' ? filters.transaction_type : undefined,
          account_code: filters.account_code !== 'all' ? filters.account_code : undefined
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotalTransactions(countResponse.data.total);
      
      // Obtener transacciones de la página actual
      const response = await axios.get(`${API}/transactions`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Transactions received:', response.data.length, 'of', countResponse.data.total);
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error) {
      toast.error('Error al cargar transacciones');
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Error al cargar tiendas');
    }
  };

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error al cargar bancos');
    }
  };

  const fetchAssetCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/asset-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssetCategories(response.data);
    } catch (error) {
      console.error('Error al cargar categorías de activos');
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Cuentas cargadas:', response.data.length);
      setAccounts(response.data);
    } catch (error) {
      console.error('Error al cargar cuentas');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error al cargar proveedores');
    }
  };

  // Verificar si la cuenta seleccionada es de proveedores
  const isSupplierAccount = () => {
    if (!formData.account_code) return false;
    const account = accounts.find(a => a.account_code === formData.account_code);
    if (!account) return false;
    // Verificar si el nombre de la cuenta contiene "proveedor" o códigos típicos de proveedores (21XX)
    const name = account.account_name.toLowerCase();
    return name.includes('proveedor') || 
           name.includes('proveed') || 
           name.includes('cuentas por pagar') ||
           name.includes('acreedores');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== INICIANDO SUBMIT ===');
    console.log('FormData:', formData);
    
    if (!formData.store_id) {
      console.error('Falta store_id');
      toast.error('Por favor selecciona una tienda');
      return;
    }
    
    if (!formData.account_code) {
      console.error('Falta account_code');
      toast.error('Por favor selecciona una cuenta contable');
      return;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      console.error('Monto inválido');
      toast.error('Por favor ingresa un monto válido');
      return;
    }

    // Validar que si el método es BANCO, se haya seleccionado un banco
    if (formData.payment_method === 'BANCO' && !formData.bank_id) {
      toast.error('Por favor selecciona un banco');
      return;
    }

    // Validar número de boleta obligatorio para INGRESOS BANCARIOS
    if (formData.payment_method === 'BANCO' && formData.transaction_type === 'INGRESO') {
      if (!formData.receipt_number || !formData.receipt_number.trim()) {
        toast.error('El número de boleta es obligatorio para ingresos bancarios');
        return;
      }
    }

    // Validar número de factura obligatorio para GASTOS
    if (formData.transaction_type === 'GASTO') {
      if (!formData.invoice_number || !formData.invoice_number.trim()) {
        toast.error('El número de factura es obligatorio para gastos');
        return;
      }
    }

    // Validar saldo suficiente para GASTOS bancarios
    if (formData.transaction_type === 'GASTO' && formData.payment_method === 'BANCO' && formData.bank_id) {
      const selectedBank = banks.find(b => b.id === formData.bank_id);
      if (selectedBank) {
        const montoTransaccion = parseFloat(formData.amount) || 0;
        if (montoTransaccion > selectedBank.current_balance) {
          toast.error(`Saldo insuficiente. Disponible: Q${selectedBank.current_balance.toLocaleString('es-GT', {minimumFractionDigits: 2})}, Solicitado: Q${montoTransaccion.toLocaleString('es-GT', {minimumFractionDigits: 2})}`);
          return;
        }
      }
    }

    // Validar campos de activo si está marcado como activo
    if (formData.is_asset) {
      if (!formData.asset_data.name || !formData.asset_data.category) {
        toast.error('Por favor completa el nombre y categoría del activo');
        return;
      }
    }

    // Validar campos de combustible si está marcado
    if (formData.is_fuel) {
      if (!formData.fuel_data.gallons || !formData.fuel_data.fuel_type) {
        toast.error('Por favor completa los galones y tipo de combustible');
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No hay token');
        toast.error('Sesión expirada. Por favor inicia sesión nuevamente');
        return;
      }
      
      const dataToSend = {
        ...formData,
        amount: parseFloat(formData.amount),
        // Enviar fecha sin conversión UTC para evitar cambio de día
        transaction_date: formData.transaction_date + 'T12:00:00'
      };

      // Si es activo, preparar los datos del activo
      if (formData.is_asset) {
        dataToSend.asset_data = {
          ...formData.asset_data,
          quantity: formData.asset_data.quantity ? parseInt(formData.asset_data.quantity) : 1,
          warranty_months: formData.asset_data.warranty_months ? parseInt(formData.asset_data.warranty_months) : null
        };
      }

      // Si es combustible, preparar los datos del combustible con cálculos
      if (formData.is_fuel) {
        dataToSend.fuel_data = {
          gallons: parseFloat(formData.fuel_data.gallons),
          fuel_type: formData.fuel_data.fuel_type,
          price_per_gallon: formData.fuel_data.price_per_gallon ? parseFloat(formData.fuel_data.price_per_gallon) : null,
          petroleum_tax: fuelCalculation.petroleum_tax,
          taxable_base: fuelCalculation.taxable_base,
          iva_amount: fuelCalculation.iva
        };
      }
      
      console.log('Datos a enviar:', JSON.stringify(dataToSend, null, 2));
      
      let response;
      if (editMode && editingTransaction) {
        // Modo edición
        console.log('URL:', `${API}/transactions/${editingTransaction.id}`);
        // No enviar asset_data en edición
        delete dataToSend.is_asset;
        delete dataToSend.asset_data;
        
        response = await axios.put(`${API}/transactions/${editingTransaction.id}`, dataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('✓ Transacción actualizada exitosamente:', response.data);
        toast.success('Transacción actualizada correctamente');
      } else {
        // Modo creación
        console.log('URL:', `${API}/transactions`);
        response = await axios.post(`${API}/transactions`, dataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('✓ Transacción creada exitosamente:', response.data);
        toast.success('Transacción registrada correctamente');
      }
      
      // Close dialog first
      setDialogOpen(false);
      setEditMode(false);
      setEditingTransaction(null);
      resetForm();
      
      // Then reload transactions
      console.log('Recargando transacciones...');
      await fetchTransactions();
      console.log('Transacciones recargadas');
    } catch (error) {
      console.error('✗ Error completo:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Error al registrar transacción');
    }
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta transacción?')) return;
    
    // SOLUCIÓN DEFINITIVA: Cerrar el diálogo y desmontar la tabla completamente
    setDialogOpen(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Eliminar transacción del estado local INMEDIATAMENTE
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      setFilteredTransactions(prev => prev.filter(t => t.id !== transactionId));
      
      // Forzar remount de la tabla cambiando el key
      setTableKey(prev => prev + 1);
      
      // Eliminar del backend en segundo plano
      await axios.delete(`${API}/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transacción eliminada correctamente');
      
      // Recargar datos completos después de 500ms
      setTimeout(() => {
        fetchTransactions();
      }, 500);
      
    } catch (error) {
      toast.error('Error al eliminar transacción');
      // Recargar en caso de error
      fetchTransactions();
    }
  };


  const handleDownloadVoucher = async (transactionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/transactions/${transactionId}/voucher`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprobante_${transactionId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Comprobante descargado correctamente');
    } catch (error) {
      toast.error('Error al generar comprobante');
    }
  };


  const resetForm = () => {
    setFormData({
      store_id: '',
      account_code: '',
      transaction_type: 'GASTO',
      description: '',
      amount: '',
      has_iva: false,
      amount_includes_iva: false,
      transaction_date: getGuatemalaDate(),
      payment_method: 'EFECTIVO',
      bank_id: '',
      receipt_number: '',
      invoice_number: '',
      supplier_id: '',
      is_asset: false,
      asset_data: {
        name: '',
        category: '',
        brand: '',
        quantity: '1',
        serial_number: '',
        warranty_months: '',
        purchase_location: '',
        notes: ''
      },
      is_fuel: false,
      fuel_data: {
        gallons: '',
        fuel_type: 'SUPER',
        price_per_gallon: ''
      }
    });
    setIvaCalculation({ base: 0, iva: 0, total: 0 });
    setFuelCalculation({ petroleum_tax: 0, taxable_base: 0, iva: 0, total: 0 });
    setAccountSearchTerm('');
    setEditMode(false);
    setEditingTransaction(null);
  };

  const handleEdit = (transaction) => {
    // Llenar el formulario con los datos de la transacción
    setFormData({
      store_id: transaction.store_id,
      account_code: transaction.account_code,
      transaction_type: transaction.transaction_type,
      description: transaction.description,
      amount: transaction.amount.toString(),
      has_iva: transaction.has_iva,
      amount_includes_iva: false,  // No guardamos este campo
      transaction_date: transaction.transaction_date.split('T')[0],
      payment_method: transaction.payment_method || 'EFECTIVO',
      bank_id: transaction.bank_id || '',
      receipt_number: transaction.receipt_number || '',  // Cargar número de boleta
      invoice_number: transaction.invoice_number || '',  // Cargar número de factura
      supplier_id: transaction.supplier_id || '',  // Cargar proveedor
      is_asset: false,  // No permitir editar activos desde aquí
      asset_data: {
        name: '',
        category: '',
        brand: '',
        quantity: '1',
        serial_number: '',
        warranty_months: '',
        purchase_location: '',
        notes: ''
      },
      is_fuel: false,  // No permitir editar combustibles desde aquí
      fuel_data: {
        gallons: '',
        fuel_type: 'SUPER',
        price_per_gallon: ''
      }
    });
    setEditMode(true);
    setEditingTransaction(transaction);
    setDialogOpen(true);
  };

  // ============= FUNCIONES PARA FACTURA MULTI-LÍNEA =============
  
  const resetMultiLineForm = () => {
    setMultiLineForm({
      store_id: '',
      transaction_type: 'GASTO',
      invoice_number: '',
      invoice_date: getGuatemalaDate(),
      payment_method: 'EFECTIVO',
      bank_id: '',
      lines: [{ account_code: '', description: '', amount: '', has_iva: false, amount_includes_iva: false }]
    });
  };

  const addMultiLineLine = () => {
    setMultiLineForm(prev => ({
      ...prev,
      lines: [...prev.lines, { account_code: '', description: '', amount: '', has_iva: false, amount_includes_iva: false }]
    }));
  };

  const removeMultiLineLine = (index) => {
    if (multiLineForm.lines.length > 1) {
      setMultiLineForm(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const updateMultiLineLine = (index, field, value) => {
    setMultiLineForm(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  const getMultiLineTotal = () => {
    return multiLineForm.lines.reduce((sum, line) => {
      const amount = parseFloat(line.amount) || 0;
      if (line.has_iva) {
        if (line.amount_includes_iva) {
          return sum + amount;
        } else {
          return sum + amount * 1.12;
        }
      }
      return sum + amount;
    }, 0);
  };

  const handleMultiLineSubmit = async (e) => {
    e.preventDefault();
    
    if (!multiLineForm.store_id) {
      toast.error('Por favor selecciona una tienda');
      return;
    }
    
    if (multiLineForm.payment_method === 'BANCO' && !multiLineForm.bank_id) {
      toast.error('Por favor selecciona un banco');
      return;
    }
    
    // Validar que todas las líneas tengan los datos requeridos
    for (let i = 0; i < multiLineForm.lines.length; i++) {
      const line = multiLineForm.lines[i];
      if (!line.account_code || !line.description || !line.amount) {
        toast.error(`Por favor completa todos los campos de la línea ${i + 1}`);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const dataToSend = {
        ...multiLineForm,
        invoice_date: multiLineForm.invoice_date + 'T12:00:00',
        lines: multiLineForm.lines.map(line => ({
          ...line,
          amount: parseFloat(line.amount)
        }))
      };
      
      const response = await axios.post(`${API}/transactions/multi-line`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(`Factura creada: ${response.data.transactions_created} líneas por Q${response.data.total_amount.toLocaleString()}`);
      setMultiLineDialogOpen(false);
      resetMultiLineForm();
      fetchTransactions();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear factura multi-línea');
    }
  };

  // ============= FIN FUNCIONES MULTI-LÍNEA =============

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Resetear a página 1 cuando cambian los filtros
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-GT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'N/A';
  };

  const getBankName = (bankId) => {
    if (!bankId) return null;
    const bank = banks.find(b => b.id === bankId);
    return bank?.bank_name || 'Banco Desconocido';
  };

  const getPaymentMethodDisplay = (transaction) => {
    if (!transaction.payment_method) {
      return <span className="text-xs text-slate-400">Sin especificar</span>;
    }

    const method = transaction.payment_method;
    
    if (method === 'EFECTIVO') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Efectivo
        </span>
      );
    } else if (method === 'BANCO') {
      const bankName = getBankName(transaction.bank_id);
      return (
        <div className="space-y-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Banco
          </span>
          {bankName && <div className="text-xs text-slate-600">{bankName}</div>}
        </div>
      );
    } else if (method === 'TARJETA_CREDITO') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Tarjeta
        </span>
      );
    }
    
    return <span className="text-xs text-slate-400">{method}</span>;
  };

  const getAccountName = (accountCode) => {
    const account = accounts.find(a => a.account_code === accountCode);
    return account?.account_name || accountCode;
  };

  const totalIngresos = filteredTransactions.filter(t => t.transaction_type === 'INGRESO').reduce((sum, t) => sum + t.total_amount, 0);
  const totalGastos = filteredTransactions.filter(t => t.transaction_type === 'GASTO').reduce((sum, t) => sum + t.total_amount, 0);
  const totalIVA = filteredTransactions.reduce((sum, t) => sum + t.iva_amount, 0);
  
  // IVA por Cobrar (de ventas/ingresos)
  const ivaPorCobrar = filteredTransactions
    .filter(t => t.transaction_type === 'INGRESO' && t.has_iva)
    .reduce((sum, t) => sum + t.iva_amount, 0);
  
  // IVA por Pagar (de compras/gastos)
  const ivaPorPagar = filteredTransactions
    .filter(t => t.transaction_type === 'GASTO' && t.has_iva)
    .reduce((sum, t) => sum + t.iva_amount, 0);

  // Filtrar cuentas según el tipo de transacción Y búsqueda
  const getFilteredAccounts = () => {
    let filtered = [];
    
    if (formData.transaction_type === 'INGRESO') {
      // Para ingresos, mostrar cuentas tipo INGRESOS (4xxx)
      filtered = accounts.filter(a => a.account_type === 'INGRESOS');
    } else {
      // Para gastos, mostrar COSTOS, GASTOS, ACTIVO y PASIVO (EXCLUIR INGRESOS)
      filtered = accounts.filter(a => {
        const code = a.account_code.trim();
        // Excluir explícitamente cuentas de tipo INGRESOS
        if (a.account_type === 'INGRESOS') return false;
        
        return a.account_type === 'COSTOS' || 
               a.account_type === 'PASIVO' ||
               code.startsWith('5') ||  // COSTOS
               code.startsWith('6') ||  // GASTOS
               code.startsWith('7') ||  // GASTOS
               code.startsWith('8') ||  // OTROS GASTOS
               (a.account_type === 'ACTIVO' && a.level >= 5);
      });
    }

    // Aplicar búsqueda por texto
    if (accountSearchTerm) {
      filtered = filtered.filter(a =>
        a.account_code.includes(accountSearchTerm) ||
        a.account_name.toLowerCase().includes(accountSearchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const selectAccount = (account) => {
    setFormData({ ...formData, account_code: account.account_code });
    setAccountSearchTerm('');
    setShowAccountDropdown(false);
  };

  const getSelectedAccountDisplay = () => {
    if (!formData.account_code) return '';
    const account = accounts.find(a => a.account_code === formData.account_code);
    return account ? `${account.account_code} - ${account.account_name}` : formData.account_code;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Transacciones</h1>
          <p className="text-slate-600 mt-1">Registro de ingresos y gastos</p>
        </div>
        <div className="flex gap-2">
          {/* Botón Buscar por Boleta */}
          <Button 
            onClick={() => setShowReceiptSearch(true)}
            variant="outline" 
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            data-testid="search-receipt-button"
          >
            <Search className="h-4 w-4 mr-2" />
            Buscar Boleta
          </Button>
          
          {/* Botón Factura Multi-línea */}
          <Button 
            onClick={() => setMultiLineDialogOpen(true)}
            variant="outline" 
            className="border-purple-500 text-purple-700 hover:bg-purple-50"
            data-testid="add-multiline-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            Factura Desglosada
          </Button>
          
          {/* Botón Transacción Simple */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-transaction-button">
                <Plus className="h-4 w-4 mr-2" />
              Nueva Transacción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Transacción' : 'Nueva Transacción'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="transaction-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="store_id">Tienda *</Label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                    required
                    data-testid="store-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Seleccionar tienda</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transaction_type">Tipo *</Label>
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => {
                      setFormData({ ...formData, transaction_type: e.target.value, account_code: '' });
                      setAccountSearchTerm('');
                      setShowAccountDropdown(false);
                    }}
                    data-testid="type-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                  >
                    <option value="">Tipo</option>
                    <option value="INGRESO">Ingreso</option>
                    <option value="GASTO">Gasto</option>
                  </select>
                </div>
              </div>
              
              {/* Selector de Cuenta Buscable */}
              <div className="space-y-2">
                <Label htmlFor="account_code">Cuenta Contable * (Busca por código o nombre)</Label>
                <div className="relative">
                  {formData.account_code ? (
                    <div className="flex items-center gap-2 p-2 border border-slate-300 rounded-lg bg-white">
                      <span className="flex-1 text-sm">{getSelectedAccountDisplay()}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, account_code: '' });
                          setAccountSearchTerm('');
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Escribe para buscar cuenta..."
                        value={accountSearchTerm}
                        onChange={(e) => {
                          setAccountSearchTerm(e.target.value);
                          setShowAccountDropdown(true);
                        }}
                        onFocus={() => setShowAccountDropdown(true)}
                        className="pl-9"
                        data-testid="account-search-input"
                      />
                    </>
                  )}
                  
                  {showAccountDropdown && !formData.account_code && (
                    <div 
                      className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white border border-slate-300 rounded-lg shadow-lg"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {getFilteredAccounts().length > 0 ? (
                        getFilteredAccounts().map(account => (
                          <div
                            key={account.account_code}
                            onClick={() => selectAccount(account)}
                            onMouseDown={(e) => e.preventDefault()}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                          >
                            <div className="font-mono text-sm font-semibold text-slate-900">{account.account_code}</div>
                            <div className="text-xs text-slate-600">{account.account_name}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-sm text-slate-500">
                          No se encontraron cuentas
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {formData.transaction_type === 'INGRESO' 
                    ? `Mostrando ${getFilteredAccounts().length} cuentas de INGRESOS`
                    : `Mostrando ${getFilteredAccounts().length} cuentas de COSTOS y GASTOS`
                  }
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción de la transacción"
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Monto (Q) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transaction_date">Fecha *</Label>
                  <Input
                    id="transaction_date"
                    type="date"
                    value={formData.transaction_date}
                    onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                    required
                    data-testid="date-input"
                  />
                </div>
              </div>

              {/* Método de Pago */}
              <div className="space-y-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <Label className="font-semibold text-slate-700">Método de Pago *</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_method: 'EFECTIVO', bank_id: '' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.payment_method === 'EFECTIVO'
                        ? 'border-indigo-600 bg-indigo-100 text-indigo-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    Efectivo
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_method: 'BANCO' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.payment_method === 'BANCO'
                        ? 'border-indigo-600 bg-indigo-100 text-indigo-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    Banco
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_method: 'TARJETA_CREDITO', bank_id: '' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.payment_method === 'TARJETA_CREDITO'
                        ? 'border-indigo-600 bg-indigo-100 text-indigo-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    Tarjeta
                  </button>
                </div>

                {/* Selector de Banco */}
                {formData.payment_method === 'BANCO' && (
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="bank_id">Seleccionar Banco *</Label>
                    <select
                      id="bank_id"
                      value={formData.bank_id}
                      onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:border-slate-600"
                    >
                      <option value="">Seleccionar banco...</option>
                      {banks.length === 0 ? (
                        <option value="none" disabled>No hay bancos disponibles</option>
                      ) : (
                        banks.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.bank_name} - {bank.account_type} ({bank.account_number}) - Saldo: Q{bank.current_balance?.toLocaleString('es-GT', {minimumFractionDigits: 2}) || '0.00'}
                          </option>
                        ))
                      )}
                    </select>
                    
                    {/* Mostrar saldo disponible del banco seleccionado */}
                    {formData.bank_id && (
                      (() => {
                        const selectedBank = banks.find(b => b.id === formData.bank_id);
                        if (!selectedBank) return null;
                        
                        const saldoDisponible = selectedBank.current_balance || 0;
                        const montoTransaccion = parseFloat(formData.amount) || 0;
                        const saldoInsuficiente = formData.transaction_type === 'GASTO' && montoTransaccion > saldoDisponible;
                        
                        return (
                          <div className={`p-3 rounded-lg border mt-2 ${
                            saldoInsuficiente 
                              ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' 
                              : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                          }`}>
                            <div className="flex justify-between items-center">
                              <span className={`text-sm font-medium ${saldoInsuficiente ? 'text-red-700 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'}`}>
                                Saldo Disponible:
                              </span>
                              <span className={`font-bold font-mono ${saldoInsuficiente ? 'text-red-700 dark:text-red-400' : 'text-blue-700 dark:text-blue-400'}`}>
                                Q{saldoDisponible.toLocaleString('es-GT', {minimumFractionDigits: 2})}
                              </span>
                            </div>
                            {saldoInsuficiente && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
                                Saldo insuficiente para esta transacción de Q{montoTransaccion.toLocaleString('es-GT', {minimumFractionDigits: 2})}
                              </p>
                            )}
                          </div>
                        );
                      })()
                    )}
                    
                    {banks.length === 0 && (
                      <p className="text-xs text-amber-600">
                        Primero debes crear un banco en la sección Bancos
                      </p>
                    )}
                    
                    {/* Número de Boleta - Solo para ingresos bancarios */}
                    {formData.transaction_type === 'INGRESO' && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <Label htmlFor="receipt_number" className="font-semibold text-amber-800 flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Número de Boleta de Depósito *
                        </Label>
                        <Input
                          id="receipt_number"
                          value={formData.receipt_number}
                          onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                          placeholder="Ej: 12345678"
                          className="mt-2 bg-white"
                          data-testid="receipt-number-input"
                        />
                        <p className="text-xs text-amber-700 mt-1">
                          Obligatorio para depósitos bancarios. No se puede repetir en el mismo banco.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Campo de Número de Factura - Solo para GASTOS */}
              {formData.transaction_type === 'GASTO' && (
                <div className="space-y-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Label htmlFor="invoice_number" className="font-semibold text-emerald-800 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Numero de Factura / Serie *
                  </Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="Ej: A-12345678 o 1234567890"
                    data-testid="invoice-number-input"
                    className="bg-white"
                  />
                  <p className="text-xs text-emerald-700">
                    Obligatorio para gastos. Este numero debe ser unico en todo el sistema para evitar facturas duplicadas.
                  </p>
                </div>
              )}

              {/* Selector de Proveedor - Solo para GASTOS con cuenta de proveedores */}
              {formData.transaction_type === 'GASTO' && isSupplierAccount() && suppliers.length > 0 && (
                <div className="space-y-2 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <Label htmlFor="supplier_id" className="font-semibold text-indigo-800 flex items-center">
                    <Receipt className="h-4 w-4 mr-2" />
                    Proveedor
                  </Label>
                  <select
                    id="supplier_id"
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                    className="w-full p-2 border border-indigo-300 rounded-md bg-white text-slate-900 focus:ring-2 focus:ring-indigo-500"
                    data-testid="supplier-select"
                  >
                    <option value="">-- Seleccionar Proveedor (Opcional) --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name} - NIT: {supplier.nit}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-indigo-700">
                    Al seleccionar un proveedor, esta compra se vinculará a su historial.
                  </p>
                </div>
              )}

              <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_iva"
                    checked={formData.has_iva}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_iva: checked, amount_includes_iva: false })}
                    data-testid="has-iva-checkbox"
                  />
                  <Label htmlFor="has_iva" className="cursor-pointer font-semibold">
                    Esta transacción incluye IVA (12%)
                  </Label>
                </div>
                
                {/* Explicación contextual del IVA */}
                {formData.transaction_type === 'INGRESO' && (
                  <p className="text-xs text-blue-600 ml-6 bg-blue-50 p-2 rounded">
                    <strong>Depósito de Ventas:</strong> Si este ingreso corresponde a ventas con factura, 
                    marca esta opción para calcular el IVA Débito Fiscal (por pagar).
                  </p>
                )}
                {formData.transaction_type === 'GASTO' && (
                  <p className="text-xs text-green-600 ml-6 bg-green-50 p-2 rounded">
                    <strong>Compras con Factura:</strong> Al marcar IVA, se calculará el IVA Crédito Fiscal 
                    (a tu favor) que puedes deducir de tus impuestos.
                  </p>
                )}
                
                {formData.has_iva && (
                  <div className="flex items-center space-x-2 ml-6">
                    <Checkbox
                      id="amount_includes_iva"
                      checked={formData.amount_includes_iva}
                      onCheckedChange={(checked) => setFormData({ ...formData, amount_includes_iva: checked })}
                      data-testid="includes-iva-checkbox"
                    />
                    <Label htmlFor="amount_includes_iva" className="cursor-pointer text-sm">
                      El monto ya incluye el IVA
                    </Label>
                  </div>
                )}
              </div>

              {/* Tabla de Cálculo de IVA */}
              {formData.amount && parseFloat(formData.amount) > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                    <Receipt className="h-4 w-4 mr-2 text-blue-600" />
                    Cálculo del IVA
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-blue-200">
                      <span className="text-sm text-slate-600">Monto Base (sin IVA):</span>
                      <span className="font-bold font-mono text-slate-900">
                        {formatCurrency(ivaCalculation.base)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-blue-200">
                      <span className="text-sm text-slate-600">
                        IVA (12%):
                        {formData.amount_includes_iva && 
                          <span className="text-xs ml-1">(calculado de {formatCurrency(parseFloat(formData.amount))})</span>
                        }
                      </span>
                      <span className="font-bold font-mono text-blue-700">
                        {formatCurrency(ivaCalculation.iva)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 bg-blue-100 px-3 rounded">
                      <span className="font-semibold text-slate-900">Total a Registrar:</span>
                      <span className="font-bold font-mono text-lg text-blue-700">
                        {formatCurrency(ivaCalculation.total)}
                      </span>
                    </div>
                  </div>
                  {formData.has_iva && (
                    <div className="mt-3 text-xs text-slate-600 bg-white p-2 rounded border border-blue-100">
                      <p className="font-semibold mb-1">Fórmula utilizada:</p>
                      {formData.amount_includes_iva ? (
                        <>
                          <p>• Base = Monto ÷ 1.12 = {formatCurrency(parseFloat(formData.amount))} ÷ 1.12</p>
                          <p>• IVA = Monto - Base = {formatCurrency(ivaCalculation.iva)}</p>
                        </>
                      ) : (
                        <>
                          <p>• IVA = Base × 0.12 = {formatCurrency(parseFloat(formData.amount))} × 0.12</p>
                          <p>• Total = Base + IVA = {formatCurrency(ivaCalculation.total)}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sección de Compra de Combustible (solo en modo creación) */}
              {!editMode && formData.transaction_type === 'GASTO' && (
                <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_fuel"
                      checked={formData.is_fuel}
                      onCheckedChange={(checked) => {
                        setFormData({ ...formData, is_fuel: checked });
                        if (!checked) {
                          // Limpiar datos de combustible si se desmarca
                          setFormData(prev => ({
                            ...prev,
                            is_fuel: false,
                            fuel_data: { gallons: '', fuel_type: 'SUPER', price_per_gallon: '' }
                          }));
                        }
                      }}
                    />
                    <Label htmlFor="is_fuel" className="cursor-pointer font-semibold flex items-center">
                      <Receipt className="h-4 w-4 mr-1" />
                      Es compra de combustible
                    </Label>
                  </div>
                  
                  {formData.is_fuel && (
                    <div className="mt-4 space-y-3 pl-6 border-l-2 border-orange-300">
                      <p className="text-xs text-orange-700 mb-3">
                        El sistema calculará automáticamente el Impuesto al Petróleo según el tipo de combustible
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Galones *</Label>
                          <Input
                            type="number"
                            step="0.001"
                            value={formData.fuel_data.gallons}
                            onChange={(e) => setFormData({
                              ...formData,
                              fuel_data: { ...formData.fuel_data, gallons: e.target.value }
                            })}
                            placeholder="Ej: 10.558"
                            className="text-sm"
                            required={formData.is_fuel}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Tipo de Combustible *</Label>
                          <select
                            value={formData.fuel_data.fuel_type}
                            onChange={(e) => setFormData({
                              ...formData,
                              fuel_data: { ...formData.fuel_data, fuel_type: e.target.value }
                            })}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            required={formData.is_fuel}
                          >
                            <option value="SUPER">Super (Q 4.70/gal)</option>
                            <option value="REGULAR">Regular (Q 4.60/gal)</option>
                            <option value="DIESEL">Diesel (Q 1.30/gal)</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Precio por Galón (Opcional)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.fuel_data.price_per_gallon}
                            onChange={(e) => setFormData({
                              ...formData,
                              fuel_data: { ...formData.fuel_data, price_per_gallon: e.target.value }
                            })}
                            placeholder="Ej: 24.37"
                            className="text-sm"
                          />
                        </div>
                      </div>

                      {/* Mostrar cálculo de impuesto al petróleo */}
                      {fuelCalculation.petroleum_tax > 0 && (
                        <div className="mt-4 bg-white p-3 rounded border border-orange-200">
                          <h4 className="font-semibold text-sm text-orange-900 mb-2">Desglose de Impuestos:</h4>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Total Factura:</span>
                              <span className="font-mono font-bold">{formatCurrency(fuelCalculation.total)}</span>
                            </div>
                            <div className="flex justify-between text-orange-700">
                              <span>(-) Impuesto al Petróleo:</span>
                              <span className="font-mono font-bold">{formatCurrency(fuelCalculation.petroleum_tax)}</span>
                            </div>
                            <div className="flex justify-between border-t border-slate-200 pt-1">
                              <span className="text-slate-600">Base Gravable IVA:</span>
                              <span className="font-mono">{formatCurrency(fuelCalculation.taxable_base)}</span>
                            </div>
                            <div className="flex justify-between text-blue-700">
                              <span>IVA (12%):</span>
                              <span className="font-mono font-bold">{formatCurrency(fuelCalculation.iva)}</span>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-2 italic">
                            * {formData.fuel_data.gallons} gal × Q {FUEL_TAX_RATES[formData.fuel_data.fuel_type]} = Q {fuelCalculation.petroleum_tax.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sección de Registro como Activo (solo en modo creación) */}
              {!editMode && formData.transaction_type === 'GASTO' && !formData.is_fuel && (
                <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_asset"
                      checked={formData.is_asset}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_asset: checked })}
                    />
                    <Label htmlFor="is_asset" className="cursor-pointer font-semibold flex items-center">
                      <Plus className="h-4 w-4 mr-1" />
                      Registrar como Activo Fijo
                    </Label>
                  </div>
                  
                  {formData.is_asset && (
                    <div className="mt-4 space-y-3 pl-6 border-l-2 border-amber-300">
                      <p className="text-xs text-amber-700 mb-3">
                        Esta transacción se registrará como un activo en el inventario de la empresa
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Nombre del Activo *</Label>
                          <Input
                            value={formData.asset_data.name}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, name: e.target.value }
                            })}
                            placeholder="Ej: Laptop HP"
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Categoría *</Label>
                          <select
                            value={formData.asset_data.category}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, category: e.target.value }
                            })}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          >
                            <option value="">Seleccionar...</option>
                            {assetCategories.map(cat => (
                              <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Marca</Label>
                          <Input
                            value={formData.asset_data.brand}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, brand: e.target.value }
                            })}
                            placeholder="Ej: HP, Dell, etc."
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Cantidad *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={formData.asset_data.quantity}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, quantity: e.target.value }
                            })}
                            placeholder="1"
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Número de Serie</Label>
                          <Input
                            value={formData.asset_data.serial_number}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, serial_number: e.target.value }
                            })}
                            placeholder="S/N del equipo"
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Lugar de Compra</Label>
                          <Input
                            value={formData.asset_data.purchase_location}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, purchase_location: e.target.value }
                            })}
                            placeholder="Proveedor/Tienda"
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Garantía (meses)</Label>
                          <Input
                            type="number"
                            value={formData.asset_data.warranty_months}
                            onChange={(e) => setFormData({
                              ...formData,
                              asset_data: { ...formData.asset_data, warranty_months: e.target.value }
                            })}
                            placeholder="12"
                            className="text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Notas Adicionales</Label>
                        <textarea
                          value={formData.asset_data.notes}
                          onChange={(e) => setFormData({
                            ...formData,
                            asset_data: { ...formData.asset_data, notes: e.target.value }
                          })}
                          placeholder="Información adicional sobre el activo..."
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="2"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full btn-primary" data-testid="save-transaction-button">
                {editMode ? 'Actualizar Transacción' : (formData.is_asset ? 'Registrar Transacción y Activo' : 'Registrar Transacción')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="stat-card hover-lift">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Ingresos</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {formatCurrency(totalIngresos)}
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Gastos</CardTitle>
            <TrendingDown className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">
              {formatCurrency(totalGastos)}
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift bg-green-50 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-green-800">IVA por Cobrar</CardTitle>
            <Receipt className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-green-700">
              {formatCurrency(ivaPorCobrar)}
            </div>
            <p className="text-xs text-green-600 mt-1">Ventas</p>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift bg-red-50 border-red-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-red-800">IVA por Pagar</CardTitle>
            <Receipt className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-red-700">
              {formatCurrency(ivaPorPagar)}
            </div>
            <p className="text-xs text-red-600 mt-1">Compras</p>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift bg-blue-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-blue-800">IVA Neto</CardTitle>
            <Receipt className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-xl font-bold ${ivaPorCobrar - ivaPorPagar >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(ivaPorCobrar - ivaPorPagar)}
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {ivaPorCobrar - ivaPorPagar >= 0 ? 'A favor' : 'Por pagar'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Búsqueda y Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Descripción, cuenta, tienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tienda</Label>
              <select
                value={filters.store_id}
                onChange={(e) => handleFilterChange({ ...filters, store_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">Todas las tiendas</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <select
                value={filters.transaction_type}
                onChange={(e) => handleFilterChange({ ...filters, transaction_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">Todos</option>
                <option value="INGRESO">Solo Ingresos</option>
                <option value="GASTO">Solo Gastos</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Cuenta Contable</Label>
              <select
                value={filters.account_code}
                onChange={(e) => handleFilterChange({ ...filters, account_code: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">Todas las cuentas</option>
                {accounts
                  .filter(acc => acc.is_active)
                  .sort((a, b) => a.account_code.localeCompare(b.account_code))
                  .map(account => (
                    <option key={account.account_code} value={account.account_code}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Listado de Transacciones ({filteredTransactions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto scrollbar-thin">
            <table key={tableKey} className="w-full" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Fecha</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Tienda</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Cuenta</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Descripción</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Pago</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Monto</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">IVA</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Tipo</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr 
                    key={transaction.id} 
                    data-transaction-id={transaction.id}
                    className="border-b border-slate-100 table-row" 
                    data-testid="transaction-row"
                  >
                    <td className="py-3 px-4 text-sm text-slate-600">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span>{formatDate(transaction.transaction_date)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-900">{getStoreName(transaction.store_id)}</td>
                    <td className="py-3 px-4 text-sm">
                      <div className="font-mono text-slate-600">{transaction.account_code}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[150px]" title={getAccountName(transaction.account_code)}>
                        {getAccountName(transaction.account_code)}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-900">{transaction.description}</td>
                    <td className="py-3 px-4 text-sm">
                      {getPaymentMethodDisplay(transaction)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-mono text-slate-900">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-mono text-blue-700">
                      {formatCurrency(transaction.iva_amount)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-mono font-semibold text-slate-900">
                      {formatCurrency(transaction.total_amount)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.transaction_type === 'INGRESO'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.transaction_type === 'INGRESO' ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-1" />
                        )}
                        {transaction.transaction_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() => handleEdit(transaction)}
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:bg-green-50"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDownloadVoucher(transaction.id)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          title="Descargar Comprobante"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(transaction.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          data-testid="delete-transaction-button"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTransactions.length === 0 && (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">
                  {searchTerm ? 'No se encontraron transacciones con ese criterio' : 'No hay transacciones para mostrar'}
                </p>
              </div>
            )}
          </div>

          {/* Controles de Paginación */}
          {totalTransactions > pageSize && (
            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div className="text-sm text-slate-600">
                Mostrando {Math.min((currentPage - 1) * pageSize + 1, totalTransactions)} - {Math.min(currentPage * pageSize, totalTransactions)} de {totalTransactions} transacciones
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-2 px-3">
                  <span className="text-sm text-slate-600">
                    Página {currentPage} de {Math.ceil(totalTransactions / pageSize)}
                  </span>
                </div>
                <Button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage >= Math.ceil(totalTransactions / pageSize)}
                  variant="outline"
                  size="sm"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============= DIÁLOGO FACTURA MULTI-LÍNEA ============= */}
      <Dialog open={multiLineDialogOpen} onOpenChange={(open) => {
        setMultiLineDialogOpen(open);
        if (!open) resetMultiLineForm();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Nueva Factura Desglosada (Multi-línea)
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMultiLineSubmit} className="space-y-6">
            {/* Encabezado de la factura */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-900 mb-3">Datos de la Factura</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Tienda *</Label>
                  <select
                    value={multiLineForm.store_id}
                    onChange={(e) => setMultiLineForm({...multiLineForm, store_id: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">Seleccionar</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <select
                    value={multiLineForm.transaction_type}
                    onChange={(e) => setMultiLineForm({...multiLineForm, transaction_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="GASTO">Gasto</option>
                    <option value="INGRESO">Ingreso</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>No. Factura</Label>
                  <Input
                    placeholder="Ej: FAC-001"
                    value={multiLineForm.invoice_number}
                    onChange={(e) => setMultiLineForm({...multiLineForm, invoice_number: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={multiLineForm.invoice_date}
                    onChange={(e) => setMultiLineForm({...multiLineForm, invoice_date: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <select
                    value={multiLineForm.payment_method}
                    onChange={(e) => setMultiLineForm({...multiLineForm, payment_method: e.target.value, bank_id: ''})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="EFECTIVO">Efectivo</option>
                    <option value="BANCO">Banco</option>
                    <option value="TARJETA_CREDITO">Tarjeta</option>
                  </select>
                </div>
                {multiLineForm.payment_method === 'BANCO' && (
                  <div className="space-y-2">
                    <Label>Banco *</Label>
                    <select
                      value={multiLineForm.bank_id}
                      onChange={(e) => setMultiLineForm({...multiLineForm, bank_id: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    >
                      <option value="">Seleccionar banco</option>
                      {banks.map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.bank_name} - {bank.account_number}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Líneas de la factura */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Líneas de la Factura</h3>
                <Button 
                  type="button" 
                  onClick={addMultiLineLine} 
                  variant="outline" 
                  size="sm"
                  className="text-purple-700 border-purple-300"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar Línea
                </Button>
              </div>
              
              {multiLineForm.lines.map((line, index) => (
                <div key={index} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">Línea {index + 1}</span>
                    {multiLineForm.lines.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeMultiLineLine(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Cuenta Contable *</Label>
                      <select
                        value={line.account_code}
                        onChange={(e) => updateMultiLineLine(index, 'account_code', e.target.value)}
                        required
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="">Seleccionar</option>
                        {accounts
                          .filter(a => {
                            if (multiLineForm.transaction_type === 'INGRESO') {
                              return a.account_type === 'INGRESOS';
                            } else {
                              return ['COSTOS', 'GASTOS', 'ACTIVO', 'PASIVO'].includes(a.account_type);
                            }
                          })
                          .map(account => (
                            <option key={account.account_code} value={account.account_code}>
                              {account.account_code} - {account.account_name}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Descripción *</Label>
                      <Input
                        placeholder="Descripción del ítem"
                        value={line.description}
                        onChange={(e) => updateMultiLineLine(index, 'description', e.target.value)}
                        required
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Monto (Q) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={line.amount}
                        onChange={(e) => updateMultiLineLine(index, 'amount', e.target.value)}
                        required
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1 flex flex-col justify-end">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={line.has_iva}
                          onCheckedChange={(checked) => updateMultiLineLine(index, 'has_iva', checked)}
                        />
                        <Label className="text-xs">IVA 12%</Label>
                      </div>
                      {line.has_iva && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={line.amount_includes_iva}
                            onCheckedChange={(checked) => updateMultiLineLine(index, 'amount_includes_iva', checked)}
                          />
                          <Label className="text-xs">Incluye IVA</Label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total de la factura */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-slate-900">Total Factura:</span>
                <span className="text-2xl font-bold text-purple-700">
                  Q {getMultiLineTotal().toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {multiLineForm.lines.length} línea(s) • IVA incluido donde aplique
              </p>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setMultiLineDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Receipt className="h-4 w-4 mr-2" />
                Guardar Factura
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda por Número de Boleta */}
      <Dialog open={showReceiptSearch} onOpenChange={setShowReceiptSearch}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-amber-600" />
              Buscar por Número de Boleta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={receiptSearchTerm}
                onChange={(e) => setReceiptSearchTerm(e.target.value)}
                placeholder="Ingresa el número de boleta..."
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && searchByReceiptNumber()}
                data-testid="receipt-search-input"
              />
              <Button 
                onClick={searchByReceiptNumber}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
            
            <p className="text-sm text-slate-500">
              Busca transacciones por número de boleta bancaria. Útil para auditorías.
            </p>
            
            {receiptSearchResults && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-900">
                    Resultados ({receiptSearchResults.count})
                  </h4>
                  <span className="text-sm text-slate-500">
                    Término: "{receiptSearchResults.search_term}"
                  </span>
                </div>
                
                {receiptSearchResults.count === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No se encontraron transacciones con ese número de boleta</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="text-left py-2 px-3 font-semibold">Boleta</th>
                          <th className="text-left py-2 px-3 font-semibold">Fecha</th>
                          <th className="text-left py-2 px-3 font-semibold">Descripcion</th>
                          <th className="text-left py-2 px-3 font-semibold">Tienda</th>
                          <th className="text-left py-2 px-3 font-semibold">Banco</th>
                          <th className="text-right py-2 px-3 font-semibold">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receiptSearchResults.results.map((trans) => (
                          <tr key={trans.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="py-2 px-3 font-mono text-amber-700 dark:text-amber-400 font-semibold">
                              {trans.receipt_number}
                            </td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-400">
                              {new Date(trans.transaction_date).toLocaleDateString('es-GT')}
                            </td>
                            <td className="py-2 px-3 text-slate-700 dark:text-slate-300 max-w-xs truncate" title={trans.description}>
                              {trans.description}
                            </td>
                            <td className="py-2 px-3">{trans.store_name}</td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-400">{trans.bank_name}</td>
                            <td className="py-2 px-3 text-right font-mono text-green-600 dark:text-green-400">
                              Q {trans.total_amount?.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowReceiptSearch(false);
                setReceiptSearchResults(null);
                setReceiptSearchTerm('');
              }}
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
